import { useQuery } from '@tanstack/react-query';
import {
  getActivityFeed,
  getDashboardCharts,
  getDashboardKpis,
  getDashboardStats,
  getDashboardSummary,
  getDashboardToday,
  getOverdueInvoices,
  getRecentActivity,
  getTeamAllocationGrid,
  getTeamTasks,
  getUpcomingPayments,
  type SummaryFilters,
} from '../lib/dashboardApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

// Per NFR-DASH-002: 5-minute stale time. Recent activity refetches more often
// for a near-real-time feed (FR-DASH-015).
const FIVE_MINUTES = 5 * 60 * 1000;

// All dashboard hooks thread the global firm switcher (multi-company)
// internally: activeCompanyId goes into the queryKey and the request, so
// switching firms refetches automatically. Team allocation stays global
// (the team is shared between firms).

export function useDashboardSummary(filters: SummaryFilters = {}) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: SummaryFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: ['dashboard', 'summary', merged],
    queryFn: () => getDashboardSummary(merged),
    staleTime: FIVE_MINUTES,
  });
}

export function useDashboardStats() {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'stats', activeCompanyId ?? null],
    queryFn: () => getDashboardStats(activeCompanyId),
    staleTime: FIVE_MINUTES,
  });
}

export function useRecentActivity(limit = 10) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'recent-activity', limit, activeCompanyId ?? null],
    queryFn: () => getRecentActivity(limit, activeCompanyId),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// --- COO Executive Command Center (CLE-256) --------------------------------

export function useDashboardKpis() {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'kpis', activeCompanyId ?? null],
    queryFn: () => getDashboardKpis(activeCompanyId),
    staleTime: FIVE_MINUTES,
  });
}

export function useDashboardToday() {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'today', activeCompanyId ?? null],
    queryFn: () => getDashboardToday(activeCompanyId),
    staleTime: 2 * 60 * 1000,
  });
}

export function useUpcomingPayments(days = 14) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'upcoming-payments', days, activeCompanyId ?? null],
    queryFn: () => getUpcomingPayments(days, activeCompanyId),
    staleTime: FIVE_MINUTES,
  });
}

export function useOverdueInvoices() {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'overdue-invoices', activeCompanyId ?? null],
    queryFn: () => getOverdueInvoices(activeCompanyId),
    staleTime: FIVE_MINUTES,
  });
}

export function useTeamAllocationGrid() {
  return useQuery({
    queryKey: ['dashboard', 'team-allocation-grid'],
    queryFn: () => getTeamAllocationGrid(),
    staleTime: FIVE_MINUTES,
  });
}

export function useActivityFeed(limit = 15) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'activity-feed', limit, activeCompanyId ?? null],
    queryFn: () => getActivityFeed(limit, activeCompanyId),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// --- Dashboard charts + per-person tasks (CLE-257) -------------------------

export function useDashboardCharts() {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'charts', activeCompanyId ?? null],
    queryFn: () => getDashboardCharts(activeCompanyId),
    staleTime: FIVE_MINUTES,
  });
}

export function useTeamTasks() {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  return useQuery({
    queryKey: ['dashboard', 'team-tasks', activeCompanyId ?? null],
    queryFn: () => getTeamTasks(activeCompanyId),
    staleTime: 2 * 60 * 1000,
  });
}
